import type { AIContentBlock, AIMessage } from "@/lib/api/ai-refill";

// AIConversation is the read-only renderer for a refill session's AI thread.
// It's shared by the wizard's assistant panel (live thread in component state)
// and the session-detail page (persisted thread from useSessionAITurns), so the
// conversation looks identical wherever it's surfaced. Tool-use / tool-result
// blocks render as compact provenance lines rather than raw JSON.
export function AIConversation({
  history,
  emptyHint = 'Conversation will appear here. Try starting with "what\'s the next step?"',
}: {
  history: AIMessage[];
  emptyHint?: string;
}) {
  if (history.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyHint}</p>;
  }
  return (
    <ol className="space-y-2">
      {history.map((m, i) => (
        <li
          key={i}
          className={
            "rounded-md border px-3 py-2 text-sm " +
            (m.role === "assistant" ? "bg-accent/30" : "bg-background")
          }
        >
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {m.role}
          </div>
          <div className="mt-1 space-y-1">
            {m.content.map((block, j) => (
              <Block key={j} block={block} />
            ))}
          </div>
        </li>
      ))}
    </ol>
  );
}

// LatestAssistantText pulls the most recent assistant text out of a thread —
// used where we want to show just the freshest reply (e.g. a wizard step's
// result, or per-dose mixing feedback) rather than the whole transcript.
export function latestAssistantText(history: AIMessage[]): string | null {
  for (let i = history.length - 1; i >= 0; i--) {
    const m = history[i];
    if (m?.role !== "assistant") continue;
    const text = m.content
      .filter((b) => b.type === "text" && b.text)
      .map((b) => b.text)
      .join("\n")
      .trim();
    if (text) return text;
  }
  return null;
}

function Block({ block }: { block: AIContentBlock }) {
  if (block.type === "text") {
    return <p className="whitespace-pre-wrap">{block.text}</p>;
  }
  if (block.type === "tool_use" && block.tool_use) {
    return (
      <div className="font-mono text-xs text-muted-foreground">
        → tool: <span className="font-medium">{block.tool_use.name}</span>
      </div>
    );
  }
  if (block.type === "tool_result" && block.tool_result) {
    const isErr = block.tool_result.is_error;
    return (
      <div
        className={
          "font-mono text-xs " +
          (isErr ? "text-destructive" : "text-muted-foreground")
        }
      >
        ← result {isErr && "(error)"}
      </div>
    );
  }
  return null;
}
